import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Card,
  Image,
  Text,
  Button,
  Group,
  Stack,
  Modal,
  TextInput,
  FileInput,
  Switch,
  ActionIcon,
  Loader,
  SimpleGrid,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconUpload, 
  IconEdit, 
  IconTrash, 
  IconEyeOff,
  IconArrowUp,
  IconArrowDown,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { galleryService, GalleryItem } from '../../services/galleryService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

type GalleryItemType = 'LOGO' | 'BANNER' | 'SLIDE' | 'ICON' | 'IMAGE' | 'VIDEO';

interface GalleryUploaderProps {
  galleryName: string;
  title: string;
  description: string;
  acceptedFileTypes?: string;
  itemType: GalleryItemType;
  multiple?: boolean;
  onUpdate?: () => void;
}

interface FormValues {
  title: string;
  description: string;
  linkUrl: string;
  altText: string;
  type: GalleryItemType;
  active: boolean;
}

export const GalleryUploader: React.FC<GalleryUploaderProps> = ({
  galleryName,
  title,
  description,
  acceptedFileTypes = 'image/png,image/jpeg,image/svg+xml,image/gif,image/webp',
  itemType,
  onUpdate,
}) => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [uploadModalOpened, { open: openUploadModal, close: closeUploadModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const form = useForm<FormValues>({
    initialValues: {
      title: '',
      description: '',
      linkUrl: '',
      altText: '',
      type: itemType,
      active: true,
    },
  });

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    fetchItems();
  }, [galleryName]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const gallery = await galleryService.getGalleryByName(galleryName);
      console.log('Fetched gallery:', gallery);
      const filteredItems = gallery.items.filter(item => item.type === itemType);
      console.log('Filtered items:', filteredItems);
      setItems(filteredItems);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load gallery items',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    // Remove any double slashes
    const cleanUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${API_URL}${cleanUrl}`;
  };

  const handleUpload = async () => {
    if (!file) {
      notifications.show({
        title: 'Error',
        message: 'Please select a file',
        color: 'red',
      });
      return;
    }

    const formData = new FormData();
    
    const data = {
      title: form.values.title || '',
      description: form.values.description || '',
      linkUrl: form.values.linkUrl || '',
      altText: form.values.altText || '',
      type: itemType,
      orderIndex: 0,
      active: form.values.active,
    };
    
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob);
    formData.append('file', file);

    try {
      setUploadLoading(true);
      const result = await galleryService.uploadGalleryItem(galleryName, formData);
      console.log('✅ Upload successful:', result);
      notifications.show({
        title: 'Success',
        message: 'Item uploaded successfully',
        color: 'green',
      });
      closeUploadModal();
      form.reset();
      setFile(null);
      fetchItems();
      onUpdate?.();
    } catch (error: any) {
      console.error('Upload error:', error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to upload item',
        color: 'red',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedItem) return;

    const formData = new FormData();
    
    const data = {
      title: form.values.title || '',
      description: form.values.description || '',
      linkUrl: form.values.linkUrl || '',
      altText: form.values.altText || '',
      type: selectedItem.type,
      orderIndex: selectedItem.orderIndex,
      active: form.values.active,
    };
    
    formData.append('data', JSON.stringify(data));
    
    if (file) {
      formData.append('file', file);
    }

    try {
      setUploadLoading(true);
      await galleryService.updateGalleryItem(selectedItem.id, formData);
      notifications.show({
        title: 'Success',
        message: 'Item updated successfully',
        color: 'green',
      });
      closeEditModal();
      form.reset();
      setFile(null);
      setSelectedItem(null);
      fetchItems();
      onUpdate?.();
    } catch (error: any) {
      console.error('Edit error:', error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to update item',
        color: 'red',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      setUploadLoading(true);
      await galleryService.deleteGalleryItem(selectedItem.id);
      notifications.show({
        title: 'Success',
        message: 'Item deleted successfully',
        color: 'green',
      });
      closeDeleteModal();
      setSelectedItem(null);
      fetchItems();
      onUpdate?.();
    } catch (error: any) {
      console.error('Delete error:', error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to delete item',
        color: 'red',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const openEdit = (item: GalleryItem) => {
    setSelectedItem(item);
    form.setValues({
      title: item.title || '',
      description: item.description || '',
      linkUrl: item.linkUrl || '',
      altText: item.altText || '',
      type: item.type as GalleryItemType,
      active: item.active,
    });
    openEditModal();
  };

  if (loading) {
    return (
      <Paper withBorder p="xl" className="text-center">
        <Loader />
      </Paper>
    );
  }

  return (
    <Paper withBorder p="xl" radius="md">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3}>{title}</Title>
          <Text size="sm" c="dimmed">{description}</Text>
        </div>
        <Button 
          leftSection={<IconUpload size={16} />}
          onClick={openUploadModal}
          color="exportGreen"
        >
          Upload New
        </Button>
      </Group>

      {items.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No items uploaded yet. Click "Upload New" to add your first item.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
          {items.sort((a, b) => a.orderIndex - b.orderIndex).map((item) => {
            const imageUrl = getFullImageUrl(item.imageUrl);
            console.log(`Rendering item ${item.title}:`, imageUrl);
            
            return (
              <Card key={item.id} withBorder padding="md" radius="md">
                <Card.Section>
                  <Image
                    src={imageUrl}
                    height={150}
                    alt={item.altText || item.title}
                    fit="cover"
                    onError={(e) => {
                      console.error(`Failed to load image for ${item.title}:`, imageUrl);
                      e.currentTarget.src = 'https://via.placeholder.com/150?text=Error';
                    }}
                    onLoad={() => console.log(`✅ Image loaded: ${item.title}`)}
                    loading="lazy" 
                  />
                </Card.Section>

                <Stack mt="md" gap="xs">
                  <Text fw={500} lineClamp={1}>{item.title || 'Untitled'}</Text>
                  <Text size="xs" c="dimmed" lineClamp={2}>{item.description}</Text>
                  
                  <Group justify="space-between" mt="xs">
                    <Group gap="xs">
                      <ActionIcon 
                        size="sm" 
                        variant="light" 
                        color="blue"
                        onClick={() => openEdit(item)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon 
                        size="sm" 
                        variant="light" 
                        color="red"
                        onClick={() => {
                          setSelectedItem(item);
                          openDeleteModal();
                        }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                    <Group gap="xs">
                      <ActionIcon size="sm" variant="subtle">
                        <IconArrowUp size={16} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle">
                        <IconArrowDown size={16} />
                      </ActionIcon>
                      {!item.active && (
                        <IconEyeOff size={16} className="text-gray-400" />
                      )}
                    </Group>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      {/* Upload Modal */}
      <Modal
        opened={uploadModalOpened}
        onClose={closeUploadModal}
        title={`Upload to ${title}`}
        size="lg"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleUpload(); }}>
          <Stack>
            <FileInput
              label="Select Image"
              placeholder="Click to select file"
              accept={acceptedFileTypes}
              onChange={setFile}
              required
            />
            
            <TextInput
              label="Title"
              placeholder="Enter title"
              {...form.getInputProps('title')}
            />
            
            <TextInput
              label="Description"
              placeholder="Enter description"
              {...form.getInputProps('description')}
            />
            
            <TextInput
              label="Alt Text"
              placeholder="Enter alt text for accessibility"
              {...form.getInputProps('altText')}
            />
            
            <TextInput
              label="Link URL"
              placeholder="https://example.com"
              {...form.getInputProps('linkUrl')}
            />
            
            <Switch
              label="Active"
              description="Enable to show this item on the website"
              {...form.getInputProps('active', { type: 'checkbox' })}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={closeUploadModal}>Cancel</Button>
              <Button type="submit" color="exportGreen" loading={uploadLoading}>Upload</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title={`Edit ${title} Item`}
        size="lg"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleEdit(); }}>
          <Stack>
            {selectedItem && (
              <Image
                src={getFullImageUrl(selectedItem.imageUrl)}
                height={150}
                fit="contain"
                mb="md"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/150?text=Error';
                }}
              />
            )}
            
            <FileInput
              label="Replace Image (optional)"
              placeholder="Click to select new file"
              accept={acceptedFileTypes}
              onChange={setFile}
            />
            
            <TextInput
              label="Title"
              placeholder="Enter title"
              {...form.getInputProps('title')}
            />
            
            <TextInput
              label="Description"
              placeholder="Enter description"
              {...form.getInputProps('description')}
            />
            
            <TextInput
              label="Alt Text"
              placeholder="Enter alt text"
              {...form.getInputProps('altText')}
            />
            
            <TextInput
              label="Link URL"
              placeholder="https://example.com"
              {...form.getInputProps('linkUrl')}
            />
            
            <Switch
              label="Active"
              {...form.getInputProps('active', { type: 'checkbox' })}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={closeEditModal}>Cancel</Button>
              <Button type="submit" color="exportGreen" loading={uploadLoading}>Update</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Confirm Deletion"
        size="md"
      >
        <Text mb="lg">
          Are you sure you want to delete this item? This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={uploadLoading}>Delete</Button>
        </Group>
      </Modal>
    </Paper>
  );
};